import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged as firebaseOnAuthStateChanged } from 'firebase/auth'
import { getAnalytics, logEvent as firebaseLogEvent } from 'firebase/analytics'
import { API_BASE_URL } from './api'

// Get Firebase config from backend API
let firebaseConfig = null

const getFirebaseConfig = async () => {
  if (firebaseConfig) return firebaseConfig

  try {
    const response = await fetch(`${API_BASE_URL}/api/firebase/config`)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (data.success) {
      firebaseConfig = data.config
      return firebaseConfig
    } else {
      throw new Error(data.error || 'Failed to get Firebase config')
    }
  } catch (error) {
    console.error('Error getting Firebase config:', error)
    throw new Error('Firebase configuration not available. Please check your backend configuration.')
  }
}

// Initialize Firebase app
let app = null
let auth = null
let googleProvider = null
let analytics = null

const initializeFirebase = async () => {
  if (app) return app

  const config = await getFirebaseConfig()
  app = initializeApp(config)
  auth = getAuth(app)
  googleProvider = new GoogleAuthProvider()

  // Initialize Analytics if measurementId is present
  if (config.measurementId) {
    analytics = getAnalytics(app)
  }

  return app
}

// Export functions that initialize Firebase if needed
export const getAuthInstance = async () => {
  if (!auth) {
    await initializeFirebase()
  }
  return auth
}

export const getGoogleProvider = async () => {
  if (!googleProvider) {
    await initializeFirebase()
  }
  return googleProvider
}

// Wrapper functions that ensure Firebase is initialized
export const signInWithGoogle = async () => {
  const auth = await getAuthInstance()
  const provider = await getGoogleProvider()
  return signInWithPopup(auth, provider)
}

export const signInWithEmail = async (email, password) => {
  const auth = await getAuthInstance()
  return signInWithEmailAndPassword(auth, email, password)
}

export const signUpWithEmail = async (email, password) => {
  const auth = await getAuthInstance()
  return createUserWithEmailAndPassword(auth, email, password)
}

export const logoutUser = async () => {
  const auth = await getAuthInstance()
  return signOut(auth)
}

export const onAuthStateChangedListener = (callback) => {
  // This will be called after Firebase is initialized
  getAuthInstance().then(auth => {
    return firebaseOnAuthStateChanged(auth, callback)
  })
}

// Helper to log analytics events
export const logEvent = async (eventName, params = {}) => {
  try {
    // 1. Try using the global gtag (from index.html)
    if (window.gtag) {
      window.gtag('event', eventName, params)
      return
    }

    // 2. Fallback to Firebase Analytics SDK
    if (!analytics) {
      await initializeFirebase()
    }
    if (analytics) {
      firebaseLogEvent(analytics, eventName, params)
    }
  } catch (error) {
    // Fail silently in production
  }
}

export default initializeFirebase
